import React from 'react';
import {
  Box,
  Icons,
  InputBase,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import StudentRow from './StudentRow';
import {
  ExtensionStudent,
  ManageExtensionStyles,
} from './ManageExtension.types';

type Props = {
  extensionName: string;
  isLoading: boolean;
  onExtensionNameChange: (value: string) => void;
  onOpenCohorts: () => void;
  onSearchChange: (value: string) => void;
  onToggleStudent: (studentId: string) => void;
  search: string;
  searchResults: ExtensionStudent[];
  selectedIds: string[];
  selectedStudents: ExtensionStudent[];
  studentsCount: number;
  styles: ManageExtensionStyles;
  unselectedCount: number;
};

const ExtensionStudentsStep = ({
  extensionName,
  isLoading,
  onExtensionNameChange,
  onOpenCohorts,
  onSearchChange,
  onToggleStudent,
  search,
  searchResults,
  selectedIds,
  selectedStudents,
  studentsCount,
  styles,
  unselectedCount,
}: Props) => {
  const theme = useTheme();
  const visibleStudents = search ? searchResults : selectedStudents;

  return (
    <>
      <Typography sx={styles.extensionCount}>
        Extension ({selectedIds.length}/{Math.max(studentsCount, 1)})
      </Typography>
      <Box>
        <InputBase
          fullWidth
          placeholder="Extension Name"
          value={extensionName}
          onChange={(event) => onExtensionNameChange(event.target.value)}
          sx={styles.nameInput}
          inputProps={{ 'aria-label': 'Extension name' }}
        />
        <Typography sx={styles.helperText}>
          Students added here will be removed from their original cohort
        </Typography>
      </Box>
      <Box sx={styles.summaryHeader}>
        <Typography component="h3" sx={styles.sectionTitle}>
          Assign Students ({selectedIds.length})
        </Typography>
        {!!unselectedCount && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              color: theme.palette.primary.main,
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
            onClick={onOpenCohorts}
          >
            {unselectedCount} in other cohorts{' '}
            <Icons.ArrowForward sx={{ height: '18px', width: '18px' }} />
          </Box>
        )}
      </Box>
      <Box sx={styles.chip}>Students</Box>
      <InputBase
        fullWidth
        placeholder="Search for students"
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        startAdornment={<Icons.Search sx={styles.searchIcon} />}
        sx={styles.searchInput}
        inputProps={{
          'aria-label': 'Search for students',
          role: 'searchbox',
        }}
      />
      {isLoading && <Typography>Loading students…</Typography>}
      <Box component="ul" sx={styles.studentList}>
        {visibleStudents.map((student) => (
          <StudentRow
            key={student.id}
            canAdd={Boolean(search)}
            isSelected={selectedIds.includes(student.id)}
            onToggle={onToggleStudent}
            student={student}
            styles={styles}
          />
        ))}
      </Box>
    </>
  );
};

export default ExtensionStudentsStep;
